import { useState } from 'react';
import type { Deck, Flashcard } from '../types';
import { Input } from './ui/Input';
import { TextArea } from './ui/TextArea';
import { Button } from './ui/Button';
import { Switch } from './ui/Switch';
import { Select } from './ui/Select';
import FlashcardCard from './FlashcardCard';
import {
  Plus,
  Save,
  X,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface DeckEditorProps {
  deck?: Deck;
  onSave: (deck: Deck) => void;
  onCancel?: () => void;
  className?: string;
}

export const DeckEditor: React.FC<DeckEditorProps> = ({
  deck,
  onSave,
  onCancel,
  className = '',
}) => {
  const [name, setName] = useState(deck?.name || '');
  const [description, setDescription] = useState(deck?.description || '');
  const [tags, setTags] = useState<string[]>(deck?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [language, setLanguage] = useState(deck?.language || 'en');
  const [isFavorite, setIsFavorite] = useState(deck?.isFavorite || false);
  const [cards, setCards] = useState<Flashcard[]>(deck?.cards || []);
  const [newCard, setNewCard] = useState({ front: '', back: '', hint: '' });
  const [cardPage, setCardPage] = useState(0);

  const cardsPerPage = 6;
  const totalPages = Math.ceil(cards.length / cardsPerPage);
  const paginatedCards = cards.slice(
    cardPage * cardsPerPage,
    (cardPage + 1) * cardsPerPage
  );

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleAddCard = () => {
    if (!newCard.front.trim() || !newCard.back.trim()) return;
    const card: Flashcard = {
      id: `card-${Date.now()}`,
      front: newCard.front.trim(),
      back: newCard.back.trim(),
      hint: newCard.hint.trim() || undefined,
      tags: tags,
      difficulty: 'medium',
      createdAt: new Date().toISOString(),
    };
    setCards([...cards, card]);
    setNewCard({ front: '', back: '', hint: '' });
  };

  const handleDeleteCard = (cardId: string) => {
    setCards(cards.filter((c) => c.id !== cardId));
  };

  const handleSave = () => {
    const savedDeck: Deck = {
      ...(deck || ({} as Deck)),
      id: deck?.id || `deck-${Date.now()}`,
      name: name.trim() || 'Untitled Deck',
      description: description.trim(),
      cards,
      tags,
      language,
      isFavorite,
      color: deck?.color || 'blue',
      source: deck?.source || 'manual',
      createdAt: deck?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onSave(savedDeck);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Deck Info */}
      <div className="space-y-4 bg-gray-50 dark:bg-gray-900/50 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Deck Info</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter deck name"
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter deck description"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Tags
            </label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add a tag"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button type="button" onClick={handleAddTag} variant="secondary" size="sm">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full"
                  >
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-end gap-4">
            <Select
              label="Language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              options={[
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Spanish' },
                { value: 'fr', label: 'French' },
                { value: 'de', label: 'German' },
                { value: 'it', label: 'Italian' },
                { value: 'pt', label: 'Portuguese' },
                { value: 'nl', label: 'Dutch' },
                { value: 'ja', label: 'Japanese' },
                { value: 'zh', label: 'Chinese' },
                { value: 'ko', label: 'Korean' },
              ]}
            />
            <div className="pb-1">
              <Switch
                checked={isFavorite}
                onChange={() => setIsFavorite(!isFavorite)}
                label="Favorite"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Existing Cards */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Cards ({cards.length})
          </h3>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCardPage(Math.max(0, cardPage - 1))}
                disabled={cardPage === 0}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {cardPage + 1} / {totalPages}
              </span>
              <button
                onClick={() => setCardPage(Math.min(totalPages - 1, cardPage + 1))}
                disabled={cardPage >= totalPages - 1}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {cards.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
            No cards yet. Add your first card below.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paginatedCards.map((card) => (
              <FlashcardCard
                key={card.id}
                card={card}
                onDelete={handleDeleteCard}
                onEdit={(c) => {
                  /* Simple inline edit could go here */
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Card */}
      <div className="space-y-4 bg-gray-50 dark:bg-gray-900/50 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Add New Card
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextArea
            label="Front (Question)"
            value={newCard.front}
            onChange={(e) => setNewCard({ ...newCard, front: e.target.value })}
            placeholder="Enter the question or prompt"
            rows={3}
          />
          <TextArea
            label="Back (Answer)"
            value={newCard.back}
            onChange={(e) => setNewCard({ ...newCard, back: e.target.value })}
            placeholder="Enter the answer"
            rows={3}
          />
        </div>
        <TextArea
          label="Hint (optional)"
          value={newCard.hint}
          onChange={(e) => setNewCard({ ...newCard, hint: e.target.value })}
          placeholder="Add a helpful hint"
          rows={2}
        />
        <Button onClick={handleAddCard} leftIcon={<Plus className="w-4 h-4" />}>
          Add Card
        </Button>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} leftIcon={<X className="w-4 h-4" />}>
            Cancel
          </Button>
        )}
        <Button onClick={handleSave} leftIcon={<Save className="w-4 h-4" />}>
          {deck ? 'Update Deck' : 'Create Deck'}
        </Button>
      </div>
    </div>
  );
};

export default DeckEditor;
